import React, { useState, useEffect } from 'react';
import { 
  Box, Typography, Paper, Table, TableBody, TableCell, 
  TableContainer, TableHead, TableRow, Button, Drawer,
  TextField, FormControl, InputLabel, Select, MenuItem,
  IconButton, Divider, CircularProgress, Alert, InputAdornment,
  Dialog, DialogTitle, DialogContent, DialogActions, Tooltip, Chip
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import { apiFetch } from '../api';

const formatDate = (dateStr) => {
  if (!dateStr) return '---';
  const [year, month, day] = dateStr.split('-');
  return `${day}/${month}/${year}`;
};

const MaterialMaster = () => {
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('Rental Material');
  
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    category: 'Rental Material',
    rate_per_day: '',
    stock_total: '',
    status: 'Available'
  });

  // Outstanding Dialog States
  const [outstandingDialog, setOutstandingDialog] = useState(false);
  const [selectedMaterial, setSelectedMaterial] = useState(null);
  const [outstandingCustomers, setOutstandingCustomers] = useState([]);
  const [outstandingLoading, setOutstandingLoading] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
        const response = await apiFetch('/api/materials');
        const data = await response.json();
        
        if (Array.isArray(data)) {
            setMaterials(data);
        } else {
            console.error("API Error Response:", data);
            setError(data.error || 'डेटा लोड करताना त्रुटी आली.');
            setMaterials([]);
        }
    } catch (err) {
        console.error(err);
        setError('डेटा लोड करताना त्रुटी आली.');
    } finally {
        setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const openOutstandingDialog = async (row) => {
    const outstanding = (row.stock_total || 0) - (row.available_stock || 0);
    if (outstanding <= 0) return;
    setSelectedMaterial(row);
    setOutstandingCustomers([]);
    setOutstandingDialog(true);
    setOutstandingLoading(true);
    try {
      const res = await apiFetch(`/api/materials/${row.id}/outstanding-customers`);
      if (res.ok) {
        const data = await res.json();
        setOutstandingCustomers(data.customers || []);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setOutstandingLoading(false);
    }
  };

  const sendWhatsAppReminder = (cust, material) => {
    if (!cust.mobile) { alert(`${cust.customer_name} यांचा मोबाईल नंबर नाही.`); return; }
    const msg =
      `नमस्ते ${cust.customer_name},\n\n` +
      `आपल्याकडे खालील साहित्य येणे बाकी आहे:\n` +
      `• ${material.name}: ${cust.pending_qty} नग (दिलेली तारीख: ${formatDate(cust.issue_date)})\n\n` +
      `आजपर्यंत (चलन #${cust.challan_id}) चे अंदाजे भाडे: ₹${cust.estimated_bill} (${cust.days_pending} दिवस)\n\n` +
      `कृपया लवकरात लवकर साहित्य परत करावे.\nधन्यवाद!`;
    window.open(`https://wa.me/91${cust.mobile}?text=${encodeURIComponent(msg)}`, '_blank');
  };

  const openDrawerForCreate = () => {
    setEditingId(null);
    setFormData({ name: '', category: 'Rental Material', rate_per_day: '', stock_total: '', status: 'Available' });
    setError('');
    setDrawerOpen(true);
  };

  const handleEdit = (row) => {
    setEditingId(row.id);
    setFormData({
      name: row.name,
      category: row.category || 'Rental Material',
      rate_per_day: row.rate_per_day,
      stock_total: row.stock_total,
      status: row.status
    });
    setError('');
    setDrawerOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('तुम्हाला नक्की हे साहित्य डिलीट करायचे आहे का?')) return;
    setLoading(true);
    try {
      const response = await apiFetch(`/api/materials/${id}`, { method: 'DELETE' });
      if (response.ok) {
        fetchData();
      } else {
        setError('डिलीट करताना अडचण आली.');
      }
    } catch (err) {
      setError('सर्व्हरशी संपर्क होऊ शकला नाही.');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!formData.name) {
        setError('कृपया साहित्याचे नाव भरा.');
        return;
    }

    setLoading(true);
    const isEditing = !!editingId;
    const endpoint = isEditing ? `/api/materials/${editingId}` : '/api/materials';
    const method = isEditing ? 'PUT' : 'POST';

    try {
        const response = await apiFetch(endpoint, {
            method,
            body: JSON.stringify(formData)
        });

        if (response.ok) {
            setDrawerOpen(false);
            setEditingId(null);
            setFormData({ name: '', category: 'Rental Material', rate_per_day: '', stock_total: '', status: 'Available' });
            fetchData();
        } else {
            setError('साहित्य जतन करताना अडचण आली.');
        }
    } catch (err) {
        setError('सर्व्हरशी संपर्क होऊ शकला नाही.');
    } finally {
        setLoading(false);
    }
  };

  const translateStatus = (status) => {
    switch(status) {
      case 'Available': return 'उपलब्ध';
      case 'Low Stock': return 'कमी स्टॉक';
      case 'Damaged': return 'खराब';
      default: return status;
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      {/* Header section */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#1B5E20' }}>
          साहित्य मास्टर
        </Typography>
        <Button 
          variant="contained" 
          onClick={openDrawerForCreate}
          sx={{ backgroundColor: '#0FA958', '&:hover': { backgroundColor: '#0C8A47' }, minWidth: 200 }}
        >
          {loading && !drawerOpen ? <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} /> : '+ नवीन साहित्य जोडा'}
        </Button>
      </Box>

      {/* Search and Filters */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        <TextField
          sx={{ width: 300, backgroundColor: '#fff', '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          variant="outlined"
          placeholder="साहित्याचे नाव शोधा..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: '#627D98' }} />
              </InputAdornment>
            ),
          }}
        />
        
        <FormControl sx={{ width: 200, backgroundColor: '#fff', borderRadius: 2 }}>
          <InputLabel>कॅटेगरी (Category)</InputLabel>
          <Select 
            value={categoryFilter} 
            label="कॅटेगरी (Category)" 
            onChange={(e) => setCategoryFilter(e.target.value)}
          >
            <MenuItem value="All">सर्व साहित्य (All)</MenuItem>
            <MenuItem value="Rental Material">Rental Material</MenuItem>
            <MenuItem value="Selling Material">Selling Material</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {error && !drawerOpen && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      {/* Main Table */}
      <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: '0 4px 10px rgba(0,0,0,0.05)', maxHeight: '70vh', overflow: 'auto' }}>
        <Table stickyHeader sx={{ minWidth: 650 }} aria-label="material table">
          <TableHead sx={{ backgroundColor: '#f0fdf4' }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>अ. क्र.</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>साहित्याचे नाव</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>कॅटेगरी</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                {categoryFilter === 'Rental Material' ? 'दर/दिवस (₹)' : 'दर (₹)'}
              </TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold' }}>सुरुवातीचा स्टॉक</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>सध्या उपलब्ध स्टॉक</TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold' }}>Outstanding स्टॉक</TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold' }}>स्थिती</TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold' }}>कृती</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && materials.length === 0 ? (
                <TableRow><TableCell colSpan={8} align="center"><CircularProgress size={24} /></TableCell></TableRow>
            ) : materials
                .filter(row => categoryFilter === 'All' || (row.category || 'Rental Material') === categoryFilter)
                .filter(row => row.name.toLowerCase().includes(searchQuery.toLowerCase()))
                .map((row, index) => (
              <TableRow
                key={row.id}
                sx={{ '&:last-child td, &:last-child th': { border: 0 }, '&:hover': { backgroundColor: '#fcfcfc' } }}
              >
                <TableCell>{index + 1}</TableCell>
                <TableCell>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>{row.name}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ color: '#555' }}>{row.category || 'Rental Material'}</Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body1">₹ {row.rate_per_day}</Typography>
                  <Typography variant="caption" sx={{ display: 'block', color: '#888' }}>
                    {(row.category || 'Rental Material') === 'Rental Material' ? 'दर/दिवस' : 'दर'}
                  </Typography>
                </TableCell>
                <TableCell align="center">{row.stock_total}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold', color: row.available_stock > 0 ? '#137333' : '#c5221f' }}>
                  {row.available_stock} नग
                </TableCell>
                <TableCell align="center">
                  {(() => {
                    const outstanding = (row.stock_total || 0) - (row.available_stock || 0);
                    if (outstanding <= 0) return <span style={{ color: '#bbb' }}>---</span>;
                    return (
                      <Tooltip title="क्लिक करा — कोणत्या customer कडे pending आहे ते पहा" arrow>
                        <Chip
                          label={`${outstanding} नग`}
                          size="small"
                          onClick={() => openOutstandingDialog(row)}
                          sx={{
                            backgroundColor: '#ffebee',
                            color: '#c62828',
                            fontWeight: 'bold',
                            border: '1px solid #ef9a9a',
                            cursor: 'pointer',
                            '&:hover': { backgroundColor: '#ffcdd2' }
                          }}
                        />
                      </Tooltip>
                    );
                  })()}
                </TableCell>
                <TableCell align="center">
                  <Box 
                    sx={{ 
                      px: 2, py: 0.5, 
                      borderRadius: 4, 
                      display: 'inline-block',
                      backgroundColor: row.status === 'Available' ? '#e6f4ea' : '#fce8e6',
                      color: row.status === 'Available' ? '#137333' : '#c5221f',
                      fontSize: '0.85rem'
                    }}
                  >
                    {translateStatus(row.status)}
                  </Box>
                </TableCell>
                <TableCell align="center">
                  <IconButton size="small" onClick={() => handleEdit(row)} sx={{ color: '#1B5E20' }}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={() => handleDelete(row.id)} sx={{ color: '#d32f2f' }}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {!loading && materials.length === 0 && (
                <TableRow><TableCell colSpan={8} align="center">अजून कोणतेही साहित्य भरले नाही.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Drawer Form */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{ sx: { width: { xs: '100%', sm: 400 } } }}
      >
        <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', height: '100%' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1B5E20' }}>
              {editingId ? 'साहित्य संपादित करा' : 'नवीन साहित्य जोडा'}
            </Typography>
            <IconButton onClick={() => setDrawerOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>

          <Divider sx={{ mb: 3 }} />
          
          {error && drawerOpen && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          <TextField 
            fullWidth label="साहित्याचे नाव" 
            variant="outlined" sx={{ mb: 2.5 }} 
            value={formData.name}
            onChange={(e) => setFormData({...formData, name: e.target.value})}
          />
          
          <FormControl fullWidth sx={{ mb: 2.5 }}>
            <InputLabel>कॅटेगरी (Category)</InputLabel>
            <Select 
                label="कॅटेगरी (Category)" 
                value={formData.category}
                onChange={(e) => setFormData({...formData, category: e.target.value})}
            >
              <MenuItem value="Rental Material">Rental Material</MenuItem>
              <MenuItem value="Selling Material">Selling Material</MenuItem>
            </Select>
          </FormControl>

          <TextField 
            fullWidth label={formData.category === 'Selling Material' ? 'दर (₹)' : 'दर प्रति दिवस (₹)'} type="number" 
            variant="outlined" sx={{ mb: 2.5 }} 
            value={formData.rate_per_day}
            onChange={(e) => setFormData({...formData, rate_per_day: e.target.value})}
          />
          <TextField 
            fullWidth label="सुरुवातीचा स्टॉक (नग)" type="number" 
            variant="outlined" sx={{ mb: 2.5 }} 
            value={formData.stock_total}
            onChange={(e) => setFormData({...formData, stock_total: e.target.value})}
          />
          
          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel>स्थिती</InputLabel>
            <Select 
                label="स्थिती" 
                value={formData.status}
                onChange={(e) => setFormData({...formData, status: e.target.value})}
            >
              <MenuItem value="Available">उपलब्ध</MenuItem>
              <MenuItem value="Low Stock">कमी स्टॉक</MenuItem>
              <MenuItem value="Damaged">खराब</MenuItem>
            </Select>
          </FormControl>

          <Box sx={{ mt: 'auto', display: 'flex', gap: 2 }}>
            <Button fullWidth variant="outlined" onClick={() => setDrawerOpen(false)} color="inherit">रद्द करा</Button>
            <Button 
              fullWidth variant="contained" 
              onClick={handleSave}
              disabled={loading}
              sx={{ backgroundColor: '#0FA958', '&:hover': { backgroundColor: '#0C8A47' }, fontWeight: 'bold' }}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : 'जतन करा'}
            </Button>
          </Box>
        </Box>
      </Drawer>

      {/* Outstanding Customers Dialog */}
      <Dialog open={outstandingDialog} onClose={() => setOutstandingDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ backgroundColor: '#B71C1C', color: '#fff', fontWeight: 'bold' }}>
          📦 Outstanding — {selectedMaterial?.name}
          <Typography variant="caption" sx={{ display: 'block', color: '#ffcdd2', fontWeight: 'normal' }}>
            एकूण pending: {outstandingCustomers.reduce((s, c) => s + c.pending_qty, 0)} नग
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 2, px: 2 }}>
          {outstandingLoading ? (
            <Box sx={{ textAlign: 'center', py: 5 }}>
              <CircularProgress sx={{ color: '#B71C1C' }} />
              <Typography sx={{ mt: 1, color: '#888' }}>डेटा लोड होत आहे...</Typography>
            </Box>
          ) : outstandingCustomers.length === 0 ? (
            <Typography align="center" color="textSecondary" sx={{ py: 4 }}>कोणतेही pending नाही.</Typography>
          ) : (
            <Table size="small" sx={{ mt: 1 }}>
              <TableHead sx={{ backgroundColor: '#ffebee' }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>ग्राहक</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>मोबाईल</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }} align="center">चलन #</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }} align="center">दिलेली तारीख</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }} align="center">Pending नग</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }} align="center">दिवस</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }} align="right">अंदाजे बिल</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }} align="center">WhatsApp</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {outstandingCustomers.map((cust, i) => (
                  <TableRow key={i} sx={{ backgroundColor: i % 2 === 0 ? '#fff' : '#fff8f8' }}>
                    <TableCell sx={{ fontWeight: 'bold', color: '#1B5E20' }}>{cust.customer_name}</TableCell>
                    <TableCell>{cust.mobile || <span style={{ color: '#bbb' }}>नाही</span>}</TableCell>
                    <TableCell align="center">#{cust.challan_id}</TableCell>
                    <TableCell align="center">{formatDate(cust.issue_date)}</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold', color: '#c62828' }}>{cust.pending_qty}</TableCell>
                    <TableCell align="center">{cust.days_pending}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>₹ {cust.estimated_bill.toLocaleString('en-IN')}</TableCell>
                    <TableCell align="center">
                      <Tooltip title={cust.mobile ? 'WhatsApp Reminder पाठवा' : 'मोबाईल नाही'} arrow>
                        <span>
                          <IconButton
                            size="small"
                            disabled={!cust.mobile}
                            onClick={() => sendWhatsAppReminder(cust, selectedMaterial)}
                            sx={{ color: cust.mobile ? '#25D366' : '#ccc' }}
                          >
                            <WhatsAppIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
                {/* Total row */}
                <TableRow sx={{ backgroundColor: '#ffebee', borderTop: '2px solid #ef9a9a' }}>
                  <TableCell colSpan={4} sx={{ fontWeight: 'bold' }}>एकूण</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', color: '#c62828', fontSize: '1rem' }}>
                    {outstandingCustomers.reduce((s, c) => s + c.pending_qty, 0)} नग
                  </TableCell>
                  <TableCell />
                  <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '1rem' }}>
                    ₹ {outstandingCustomers.reduce((s, c) => s + c.estimated_bill, 0).toLocaleString('en-IN')}
                  </TableCell>
                  <TableCell />
                </TableRow>
              </TableBody>
            </Table>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOutstandingDialog(false)} variant="outlined">बंद करा</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MaterialMaster;
